import { alpha, Tooltip, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TSpaceBase } from '@onesource/schemas';
import { SpaceStatus, SpaceType } from '@onesource/schemas';
import { useNavigate } from '@tanstack/react-router';
import { MdCheck, MdLock, MdTaskAlt } from 'react-icons/md';
import { DraftBadge } from 'src/components/draft-badge';
import { appKeyToSlug } from 'src/config';

import { shimmer } from '#/pages/program-card';

import { useContentRouteOptional } from '../hooks/use-content-route';
import { usePreviewMode } from '../hooks/use-preview-mode';

interface SpaceModuleCardProps {
  module: TSpaceBase;
  parentSpaceId: string;
  index: number;
  isLocked?: boolean;
  prerequisiteName?: string | null;
  showIndexBadge?: boolean;
}

/**
 * Space Module Card Component
 * Displays a module card in the space home page
 * Based on Figma design
 */
export const SpaceModuleCard = ({
  module,
  parentSpaceId,
  index,
  isLocked = false,
  prerequisiteName,
  showIndexBadge = true,
}: SpaceModuleCardProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const content = useContentRouteOptional();
  const { isPreview } = usePreviewMode();
  const isDraftModule = module.status === SpaceStatus.DRAFT;
  // "My Analytics" shares the card but has no lessons to count; it shows what it is instead.
  const isAnalytics = module.spaceType === SpaceType.ANALYTICS;

  const handleClick = () => {
    if (isLocked) {
      return;
    }
    // Go straight to the slug URL (program slug from context, module slug from the
    // entity) to skip the legacy UUID route's redirect flash; fall back otherwise.
    if (content?.programSlug && module.slug) {
      navigate({
        to: '/programs/$programSlug/$moduleSlug',
        params: { programSlug: content.programSlug, moduleSlug: appKeyToSlug(module.slug) },
      });
      return;
    }
    navigate({ to: `/community-hub/spaces/${parentSpaceId}/modules/${module.id}` });
  };

  const lockMessage = prerequisiteName ? `Complete "${prerequisiteName}" to unlock` : 'Locked';

  // Build lesson count text
  const getLessonCountText = () => {
    if (module.lessonCount === undefined || module.lessonCount === 0) {
      return null;
    }
    if (module.lessonCount === 1) {
      return 'Lesson 1';
    }
    return `Module 1 to ${module.lessonCount}`;
  };

  const lessonCountText = isAnalytics ? 'Your requirements and progress' : getLessonCountText();

  return (
    <Tooltip title={isLocked ? lockMessage : ''} placement="top" arrow disableHoverListener={!isLocked}>
      <Box
        onClick={handleClick}
        sx={{
          bgcolor: 'white',
          borderRadius: '15px',
          padding: '20px',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          cursor: isLocked ? 'not-allowed' : 'pointer',
          opacity: isLocked ? 0.5 : 1,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': isLocked
            ? undefined
            : {
                transform: 'translateY(-2px)',
                bgcolor: theme.palette.grey[50],
                boxShadow: theme =>
                  `0 10px 20px ${alpha(theme.palette.grey[400], 0.25)}, 0 10px 20px ${alpha(
                    theme.palette.grey[400],
                    0.15,
                  )}`,
                '&::before': {
                  animation: `${shimmer} 2s linear infinite`,
                },
              },
        }}
      >
        {/* Number Badge */}
        {showIndexBadge && (
          <Box
            sx={{
              width: 43,
              height: 43,
              borderRadius: '11px',
              bgcolor: '#eaeee8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {isAnalytics ? (
              <MdTaskAlt size={26} color={theme.palette.primary.main} />
            ) : (
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 30, md: 30 },
                  lineHeight: { xs: undefined, md: '30px' },
                  background:
                    'linear-gradient(90deg, rgb(0, 0, 0) 0%, rgb(0, 0, 0) 100%), linear-gradient(rgb(255, 107, 157) 0%, rgb(196, 69, 105) 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  textAlign: 'center',
                }}
              >
                {index}
              </Typography>
            )}
          </Box>
        )}

        {/* Module Info */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {/* Module Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 20, md: 20 },
                lineHeight: { xs: undefined, md: '24px' },
                color: 'black',
              }}
            >
              {module.name}
            </Typography>
            {isPreview && isDraftModule && <DraftBadge />}
          </Box>

          {/* Locked prerequisite hint */}
          {isLocked && (
            <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <MdLock size={13} color={theme.palette.grey[600]} />
              <Typography
                sx={{
                  fontFamily: '"Figtree", sans-serif',
                  fontWeight: 600,
                  fontSize: 13,
                  lineHeight: '18px',
                  color: theme.palette.grey[600],
                }}
              >
                {lockMessage}
              </Typography>
            </Box>
          )}

          {/* Description/Meta Info */}
          {!isLocked && module.showSubtitle !== false && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {/* Lesson Count */}
              {lessonCountText && (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', pt: '3px' }}>
                    <MdCheck size={12} color={theme.palette.primary.main} />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: '"Figtree", sans-serif',
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: '19px',
                      color: 'black',
                      flex: 1,
                    }}
                  >
                    {lessonCountText}
                  </Typography>
                </Box>
              )}

              {/* Downloadable Assets */}
              {module.hasDownloadableAssets && (
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', pt: '3px' }}>
                    <MdCheck size={12} color={theme.palette.primary.main} />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: '"Figtree", sans-serif',
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: '19px',
                      color: 'black',
                      flex: 1,
                    }}
                  >
                    Downloadable assets
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Trailing lock icon */}
        {isLocked && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <MdLock size={20} color={theme.palette.grey[500]} />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};
